import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Placement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  title: React.ReactNode;
  placement?: Placement;
  children: React.ReactNode;
  className?: string;
}

export default function Tooltip({ title, placement = 'top', children, className }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const hideTimeout = useRef<number | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (hideTimeout.current) window.clearTimeout(hideTimeout.current);
    };
  }, []);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const margin = 8;

    switch (placement) {
      case 'top':
        setCoords({ x: rect.left + rect.width / 2, y: rect.top - margin });
        break;
      case 'bottom':
        setCoords({ x: rect.left + rect.width / 2, y: rect.bottom + margin });
        break;
      case 'left':
        setCoords({ x: rect.left - margin, y: rect.top + rect.height / 2 });
        break;
      case 'right':
        setCoords({ x: rect.right + margin, y: rect.top + rect.height / 2 });
        break;
    }
  }, [placement]);

  const show = () => {
    if (hideTimeout.current) window.clearTimeout(hideTimeout.current);
    updatePosition();
    setVisible(true);
  };

  const hide = () => {
    hideTimeout.current = window.setTimeout(() => setVisible(false), 100);
  };

  useEffect(() => {
    if (!visible) return;

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible, updatePosition]);

  // After the tooltip renders, clamp it so it stays fully within the viewport.
  useEffect(() => {
    if (!visible || !tooltipRef.current) return;

    const tip = tooltipRef.current;
    const tipRect = tip.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const screenPadding = 8;

    let { left, top } = tipRect;

    if (left < screenPadding) left = screenPadding;
    if (top < screenPadding) top = screenPadding;
    if (left + tipRect.width > vw - screenPadding) left = vw - screenPadding - tipRect.width;
    if (top + tipRect.height > vh - screenPadding) top = vh - screenPadding - tipRect.height;

    tip.style.left = `${left}px`;
    tip.style.top = `${top}px`;
    tip.style.transform = 'none';
  }, [visible, coords]);

  const transform = useMemo(() => {
    switch (placement) {
      case 'top':
        return 'translate(-50%, -100%)';
      case 'bottom':
        return 'translate(-50%, 0)';
      case 'left':
        return 'translate(-100%, -50%)';
      case 'right':
        return 'translate(0, -50%)';
      default:
        return 'translate(-50%, -100%)';
    }
  }, [placement]);

  const tooltipNode = visible ? (
    <div
      ref={tooltipRef}
      role="tooltip"
      style={{
        position: 'fixed',
        top: coords.y,
        left: coords.x,
        transform,
        whiteSpace: 'nowrap',
        zIndex: 99999
      }}
      className="bg-gradient-to-br font-gilroy-medium py-[6px] px-[10px] from-gray-100 to-gray-300/50 shadow-md backdrop-blur-sm text-sm border border-gray-200 rounded-lg"
      onMouseEnter={() => hideTimeout.current && window.clearTimeout(hideTimeout.current)}
      onMouseLeave={hide}
    >
      <div>{title}</div>
    </div>
  ) : null;

  return (
    <>
      <span
        ref={triggerRef}
        className={className || 'inline-block'}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {children}
      </span>
      {tooltipNode && createPortal(tooltipNode, document.body)}
    </>
  );
}
